import { reportError } from "@/utils/reportError";
import { supabase } from '@/utils/supabase';
import { getTodayCH } from '@/utils/date';
import { useCallback, useEffect, useState } from 'react';

interface SpinResult {
  success: boolean;
  alreadySpun?: boolean;
}

/**
 * Verrou « 1 tour de roue par jour » via la table wheel_spins
 * (unique (user_id, spin_date), spin_date forcé serveur). Sert à empêcher
 * le farming de points : un seul tour crédité par jour.
 */
export function useWheelSpin() {
  const [spunToday, setSpunToday] = useState(false);
  // Vrai si l'utilisateur a AU MOINS un tour enregistré (toutes dates).
  // Sert au « 1ᵉʳ tour = cadeau garanti » (utils/wheel.ts).
  const [hasEverSpun, setHasEverSpun] = useState(true); // défaut prudent : pas de cadeau garanti tant que non vérifié
  const [loading, setLoading] = useState(true);

  const checkSpun = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setSpunToday(false);
        return;
      }

      // Un seul appel : dernier tour enregistré, toutes dates confondues.
      // → spunToday (verrou du jour) + hasEverSpun (1ᵉʳ tour garanti).
      const { data, error } = await supabase
        .from('wheel_spins')
        .select('spin_date')
        .eq('user_id', user.id)
        .order('spin_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        reportError(error);
        return;
      }
      setHasEverSpun(!!data);
      setSpunToday(data?.spin_date === getTodayCH());
    } catch (err) {
      reportError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSpun();
  }, [checkSpun]);

  /**
   * Enregistre le tour du jour. `pointsWon` = points gagnés (0 pour un
   * cadeau). Retourne alreadySpun=true si l'utilisateur a déjà tourné
   * aujourd'hui (unique violation), pour ne pas créditer deux fois.
   */
  const recordSpin = useCallback(async (pointsWon: number): Promise<SpinResult> => {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return { success: false };

    const { error } = await supabase
      .from('wheel_spins')
      .insert({ user_id: user.id, points_won: pointsWon });

    if (error) {
      if (error.code === '23505') {
        setSpunToday(true);
        setHasEverSpun(true);
        return { success: true, alreadySpun: true };
      }
      reportError(error);
      return { success: false };
    }

    setSpunToday(true);
    setHasEverSpun(true);
    return { success: true };
  }, []);

  return { spunToday, hasEverSpun, loading, recordSpin, refetch: checkSpun };
}
